<script setup lang="ts">
import type { IMessage } from '~/types'
const form = ref<IMessage>({
  firstname: '',
  lastname: '',
  email: '',
  company: '',
  phone: '',
  message: '',
  isViewed: false,
  category: '',
});

const route = useRoute();

onMounted(() => {
  const categoryParam = route.query.category as string;
  if (categoryParam && categories.includes(categoryParam)) {
    form.value.category = categoryParam;
  }
});

const categories = [
  'Devenir Adhérents',
  'Recevoir le Catalogue',
  'Devenir Prestataires',
  'Devenir Partenaire',
  'Faire un don',
  'Autres'
];

const toast = useToast()
const { t } = useI18n()
const loadingCreate = ref(false);

function formatPhoneNumber(value: string) {
  const cleanValue = value.replace(/[^0-9+]/g, '');
  if (cleanValue && !cleanValue.startsWith('+')) {
    return '+' + cleanValue;
  }
  return cleanValue;
}

function onPhoneInput(event: Event) {
  const target = event.target as HTMLInputElement;
  const formattedValue = formatPhoneNumber(target.value);
  form.value.phone = formattedValue;
  target.value = formattedValue;
}

function resetForm() {
  form.value = {
    firstname: '',
    lastname: '',
    email: '',
    company: '',
    phone: '',
    message: '',
    isViewed: false,
    category: '',
  }
}

async function handleFormSubmit() {
  try {
    if (form.value.firstname === '' || form.value.lastname === '' || form.value.email === '' || form.value.message === '' || form.value.category === '') {
      toast.add({
        id: "error",
        color: "red",
        icon: "i-heroicons-x-circle",
        title: t('contact.an-error-occurred'),
        description: t('contact.please-fill-all-fields'),
      })
      return;
    }
    loadingCreate.value = true;
    await useFetch("/api/messages/createMessage", {
      method: "POST",
      body: form.value,
      onResponse({response}) {
        if (response.status === 200) {
          toast.add({
            id: "success",
            color: "green",
            icon: "i-heroicons-check-circle",
            title: t('contact.success'),
            description: t('contact.your-message-has-been-sent'),
          })
          resetForm();
          window.dispatchEvent(new CustomEvent('message-updated'));
        } else {
          toast.add({
            id: "error",
            color: "red",
            icon: "i-heroicons-x-circle",
            title: t('contact.an-error-occurred'),
            description: t('contact.please-try-again'),
          })
        }
      }
    });
  } catch (e: any) {
    const { handleApiError } = useErrorHandler()
    handleApiError(e, 'envoi du message')
  } finally {
    loadingCreate.value = false;
  }
}

useHead({
  link: [
    {
      rel: "preload",
      href: "/assets/cf03.webp",
    },
  ]
})
</script>

<template>
  <div class="relative bg-white pt-10 pb-24 sm:pb-32 lg:py-24">
    <div class="lg:absolute lg:inset-0 lg:left-1/2">
      <NuxtImg class="h-64 w-full bg-gray-50 object-cover sm:h-80 lg:absolute lg:h-full" src="/assets/cf03.webp" data-aos="zoom-in" data-aos-once="true" alt="" />
    </div>
    <div class="pb-24 pt-16 sm:pb-32 sm:pt-24 lg:mx-auto lg:grid lg:max-w-7xl lg:grid-cols-2 lg:pt-32">
      <div class="px-6 lg:px-8">
        <div class="mx-auto max-w-xl lg:mx-0 lg:max-w-lg">
          <h2 class="text-3xl tracking-tight text-gray-900">{{ t('contact.title') }}</h2>
          <p class="mt-2 text-sm leading-8 text-gray-600">Ce formulaire vous permet de contacter l'équipe de Charlys Family</p>
          <form @submit.prevent="handleFormSubmit" class="mt-16">
            <div class="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
              <div>
                <label for="firstname" class="block text-sm font-semibold leading-6 text-gray-900">{{ t('contact.firstname') }}</label>
                <div class="mt-2.5">
                  <input v-model="form.firstname" type="text" name="firstname" id="firstname" autocomplete="given-name" class="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-greenCharlys-30 sm:text-sm sm:leading-6" required />
                </div>
              </div>
              <div>
                <label for="lastname" class="block text-sm font-semibold leading-6 text-gray-900">{{ t('contact.lastname') }}</label>
                <div class="mt-2.5">
                  <input v-model="form.lastname" type="text" name="lastname" id="last-name" autocomplete="family-name" class="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-greenCharlys-30 sm:text-sm sm:leading-6" required />
                </div>
              </div>
              <div class="sm:col-span-2">
                <label for="email" class="block text-sm font-semibold leading-6 text-gray-900">{{ t('contact.email') }}</label>
                <div class="mt-2.5">
                  <input v-model="form.email" id="email" name="email" type="email" autocomplete="email" class="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-greenCharlys-30 sm:text-sm sm:leading-6" required />
                </div>
              </div>
              <div class="sm:col-span-2">
                <div class="flex justify-between text-sm leading-6">
                  <label for="company" class="block text-sm font-semibold leading-6 text-gray-900">{{ t('contact.company') }}</label>
                  <p id="phone-description" class="text-gray-400">{{ t('contact.optional') }}</p>
                </div>
                <div class="mt-2.5">
                  <input v-model="form.company" type="text" name="company" id="company" autocomplete="organization" class="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-greenCharlys-30 sm:text-sm sm:leading-6" />
                </div>
              </div>
              <div class="sm:col-span-2">
                <div class="flex justify-between text-sm leading-6">
                  <label for="phone" class="block font-semibold text-gray-900">{{ t('contact.phone') }}</label>
                  <p id="phone-description" class="text-gray-400">{{ t('contact.optional') }}</p>
                </div>
                <div class="mt-2.5">
                  <input v-model="form.phone" @input="onPhoneInput" type="tel" name="phone" id="phone" autocomplete="tel" aria-describedby="phone-description" class="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-greenCharlys-35 sm:text-sm sm:leading-6" />
                </div>
              </div>
              <div class="sm:col-span-2">
                <label for="category" class="block text-sm font-semibold leading-6 text-gray-900">{{ t('contact.category') }}</label>
                <div class="mt-2.5">
                  <select v-model="form.category" id="category" name="category" class="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-greenCharlys-35 sm:text-sm sm:leading-6" required>
                    <option value="">{{ t('contact.select-category') }}</option>
                    <option v-for="category in categories" :key="category" :value="category">{{ category }}</option>
                  </select>
                </div>
              </div>
              <div class="sm:col-span-2">
                <div class="flex justify-between text-sm leading-6">
                  <label for="message" class="block text-sm font-semibold leading-6 text-gray-900">{{ t('contact.how-can-we-help') }}</label>
                  <p id="message-description" class="text-gray-400">{{ t('contact.max-500') }}</p>
                </div>
                <div class="mt-2.5">
                  <textarea v-model="form.message" id="message" name="message" rows="4" aria-describedby="message-description" class="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-greenCharlys-35 sm:text-sm sm:leading-6" required />
                </div>
              </div>
            </div>
            <div class="mt-10 flex justify-end border-t border-gray-900/10 pt-8">
              <UButton v-if="loadingCreate" loading>{{ t('contact.loading') }}</UButton>
              <UButton v-if="!loadingCreate" type="submit">{{ t('contact.send-message') }}</UButton>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>
